import { useState, useEffect } from 'react';
import {
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  IProductReference
} from '../../../../../../../types/services';
import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import { XSign } from 'assets/images/components/NudosComponents';
import ToolsSearchDropdown from './components/ToolsSearchDropdown/ToolsSearchDropdown';
import MultipleSerialToolCard from 'components/MultipleSerialToolCard/MultipleSerialToolCard';
import useDebounce from 'hooks/useDebounce';
import useDeliveredToolsState, { SECTIONS_STEPS } from '../../../../../../../state/useDeliveredToolsState';
import { LogisticsOriginAndDestinationInfo } from '../LogisticsOriginAndDestinationInfo/LogisticsOriginAndDestinationInfo';
import { buildAddExternalProductsDTO } from '../../../../../../../utils/logisticServiceOriginDestination.utils';
import { BordOneToneIcon, NudosGenericModal, NudosStaticAlertInComponent } from 'components/NudosComponents';
import BordAnimationLoader from 'components/BordAnimationLoader/BordAnimationLoader';
import {
  searchReferenceProducts,
  addExternalProductsToLogisticService,
  getLogisticServiceDetailsById
} from '../../../../../../../services/services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';

import './AddAdditionalToolsSection.scss';

interface IConfirmationModalProps {
  verificationText: string;
  handleClose: () => void;
  handleConfirmation: () => void;
  isLoading: boolean;
}

const ConfirmationModal = ({
  verificationText,
  handleClose,
  handleConfirmation,
  isLoading
}: IConfirmationModalProps) => {
  return (
    <NudosGenericModal
      modalContent={
        <div className="modalContainer" onClick={event => event.stopPropagation()}>
          <div className="modalIconContainer">
            <BordOneToneIcon variant="triangleAlertCircle" customWidth="46px" />
          </div>
          <p className="modalText">{verificationText}</p>
          <div className="modalButtonsContainer">
            <NudosButton
              handleClick={handleClose}
              colorPalette="secondary"
              buttonText="No, Cancelar"
              customClassName="p-2"
              customWidth="180px"
              customHeight="38px"
            />
            <NudosButton
              handleClick={handleConfirmation}
              buttonText="Sí, agregar herramientas"
              customClassName="p-2"
              customWidth="180px"
              customHeight="38px"
              isButtonLoading={isLoading}
              isButtonDisabled={isLoading}
            />
          </div>
        </div>
      }
      closeModalCallback={handleClose}
      clickOutsideCallback={handleClose}
    />
  );
};

interface ISelectedToolState {
  tool: IProductReference;
  quantity: number;
  serials: string[];
}

interface AddAdditionalToolsSectionProps {
  closeModalCallback: () => void;
  cardData: IlogisticServiceTrelloCardData;
  serviceDetails: IlogisticServiceDetails;
  isUpdateTools?: boolean;
}

const AddAdditionalToolsSection = ({
  closeModalCallback,
  cardData,
  serviceDetails,
  isUpdateTools = false
}: AddAdditionalToolsSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTools, setAvailableToolsLocal] = useState<IProductReference[]>([]);
  const [selectedToolsState, setSelectedToolsState] = useState<ISelectedToolState[]>([]);
  const [serialErrors, setSerialErrors] = useState<Set<string>>(new Set());
  const [serialValidating, setSerialValidating] = useState<Set<string>>(new Set());
  const [duplicateSerialKeys, setDuplicateSerialKeys] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);

  const { setCurrentStep, setAvailableTools, setSelectedTools, setUnselectedTools, markedNotDeliveredToolIds } =
    useDeliveredToolsState();

  const selectedToolIds = new Set(selectedToolsState.map(item => item.tool.id));

  const checkIfNextButtonShouldBeDisabled = (): boolean => {
    if (isSubmitting) return true;
    if (selectedToolsState.length === 0) return true;

    const hasToolsWithoutSerial = selectedToolsState?.some(toolState =>
      toolState?.serials?.some(serial => !serial || serial?.trim() === '')
    );
    if (hasToolsWithoutSerial) return true;

    if (serialErrors.size > 0) return true;
    if (serialValidating.size > 0) return true;
    if (duplicateSerialKeys.size > 0) return true;

    return false;
  };

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
  };

  const updateToolsAfterAddingExternalProducts = async (): Promise<void> => {
    if (!serviceDetails?.id) return;

    try {
      const updatedServiceDetails = await getLogisticServiceDetailsById(serviceDetails.id);

      if (updatedServiceDetails?.tools) {
        if (isUpdateTools) {
          setAvailableTools(updatedServiceDetails.tools);
          const selectedIndices: number[] = [];
          const unselectedIndices: number[] = [];

          updatedServiceDetails.tools.forEach((tool, index) => {
            if (tool.isMarkedNotDelivered === true) {
              unselectedIndices.push(index);
            } else {
              selectedIndices.push(index);
            }
          });

          setSelectedTools(selectedIndices);
          setUnselectedTools(unselectedIndices);
        } else {
          const onlyDeliverableTools = updatedServiceDetails.tools.filter(tool => {
            return !markedNotDeliveredToolIds.includes(tool?.productId as number);
          });
          setAvailableTools(onlyDeliverableTools);

          const allIndices = onlyDeliverableTools.map((_, index) => index);
          setSelectedTools([]);
          setUnselectedTools(allIndices);
        }
      }
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener los detalles actualizados del servicio</>
      });
    }
  };

  const transformSelectedToolsForDTO = (): { tools: IProductReference[]; serials: { [key: number]: string } } => {
    const tools: IProductReference[] = [];
    const serials: { [key: number]: string } = {};

    let globalIndex = 0;
    selectedToolsState?.forEach(toolState => {
      toolState?.serials?.forEach(serial => {
        tools.push(toolState.tool);
        serials[globalIndex] = serial;
        globalIndex++;
      });
    });

    return { tools, serials };
  };

  const handleConfirmAddTools = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const { tools, serials } = transformSelectedToolsForDTO();
      const dto = buildAddExternalProductsDTO(serviceDetails, tools, serials);

      if (!dto) {
        displayErrorNotification({
          customJSXMessage: <>Error: Faltan datos requeridos para agregar las herramientas</>
        });
        return;
      }
      const response = await addExternalProductsToLogisticService(dto);

      const { createdRecords } = response || {};
      if (createdRecords === 0) {
        displayErrorNotification({
          customJSXMessage: <>Error al agregar herramientas</>
        });
        return;
      }

      await updateToolsAfterAddingExternalProducts();
      displaySuccessNotification({
        customJSXMessage: <>{`${createdRecords} herramientas agregadas exitosamente`}</>
      });
      setShowConfirmationModal(false);
      setCurrentStep(SECTIONS_STEPS.SELECTION);
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al agregar herramientas adicionales</>
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onBack = () => {
    setCurrentStep(SECTIONS_STEPS.SELECTION);
  };

  const getContent = (isUpdateTools: boolean) => {
    if (isUpdateTools) {
      return {
        additionalToolsTitle: `Agrega las herramientas que llegarán adicionales`,
        dropdownTitle: `Busca y selecciona las herramientas que llegarán adicionales`,
        nextButton: {
          text: 'Agregar herramientas',
          action: () => handleShowConfirmationModal(),
          disabledCondition: () => checkIfNextButtonShouldBeDisabled(),
          isLoading: isSubmitting
        },
        backButton: {
          text: 'Volver',
          action: () => onBack()
        }
      };
    }

    return {
      additionalToolsTitle: `Pasar a entregado`,
      dropdownTitle: `Busca y selecciona las herramientas que llegarón adicionalmente`,
      nextButton: {
        text: 'Agregar herramientas',
        action: () => handleShowConfirmationModal(),
        disabledCondition: () => checkIfNextButtonShouldBeDisabled(),
        isLoading: isSubmitting
      },
      backButton: {
        text: 'Volver',
        action: () => onBack()
      }
    };
  };

  const content = getContent(isUpdateTools);

  const handleToolToggle = (tool: IProductReference) => {
    setSelectedToolsState(prev => {
      const existingIndex = prev.findIndex(item => item.tool.id === tool.id);

      if (existingIndex >= 0) {
        const newState = [...prev];
        newState.splice(existingIndex, 1);
        return newState;
      }

      return [...prev, { tool, quantity: 1, serials: [''] }];
    });
  };

  const handleQuantityChange = (toolIndex: number, quantity: number) => {
    setSelectedToolsState(prev => {
      const newState = [...prev];
      const currentSerials = newState[toolIndex].serials;

      const newSerials = [...currentSerials];
      while (newSerials.length < quantity) newSerials.push('');
      while (newSerials.length > quantity) newSerials.pop();

      newState[toolIndex] = { ...newState[toolIndex], quantity, serials: newSerials };
      return newState;
    });
  };

  const handleSerialsChange = (toolIndex: number, serials: string[]) => {
    setSelectedToolsState(prev => {
      const newState = [...prev];
      newState[toolIndex] = { ...newState[toolIndex], serials };
      return newState;
    });
  };

  const addOrRemoveKey = (prevSet: Set<string>, key: string, shouldAdd: boolean): Set<string> => {
    const newSet = new Set(prevSet);
    if (shouldAdd) newSet.add(key);
    else newSet.delete(key);
    return newSet;
  };

  const handleSerialErrorChange = (toolIndex: number, serialIndex: number, hasError: boolean) => {
    const key = `${toolIndex}-${serialIndex}`;
    setSerialErrors(prev => addOrRemoveKey(prev, key, hasError));
  };

  const handleSerialValidatingChange = (toolIndex: number, serialIndex: number, isValidating: boolean) => {
    const key = `${toolIndex}-${serialIndex}`;
    setSerialValidating(prev => addOrRemoveKey(prev, key, isValidating));
  };

  const adjustIndicesAfterRemoval = (prevSet: Set<string>, removedIndex: number): Set<string> => {
    const newSet = new Set<string>();
    prevSet.forEach(key => {
      const [idx, serialIdx] = key.split('-').map(Number);
      if (idx < removedIndex) newSet.add(key);
      else if (idx > removedIndex) newSet.add(`${idx - 1}-${serialIdx}`);
    });
    return newSet;
  };

  const removeTool = (toolIndex: number) => {
    setSelectedToolsState(prev => {
      const newState = [...prev];
      newState.splice(toolIndex, 1);
      return newState;
    });

    setSerialErrors(prev => adjustIndicesAfterRemoval(prev, toolIndex));
    setSerialValidating(prev => adjustIndicesAfterRemoval(prev, toolIndex));
  };

  const findDuplicateSerialKeys = (): Set<string> => {
    const serialOccurrences = new Map<string, string[]>();
    const duplicates = new Set<string>();

    selectedToolsState.forEach((toolState, toolIndex) => {
      toolState.serials.forEach((serial, serialIndex) => {
        if (!serial || serial.trim() === '') return;

        const normalizedSerial = serial.trim().toLowerCase();
        const key = `${toolIndex}-${serialIndex}`;

        if (!serialOccurrences.has(normalizedSerial)) {
          serialOccurrences.set(normalizedSerial, []);
        }
        serialOccurrences.get(normalizedSerial)!.push(key);
      });
    });

    serialOccurrences.forEach(keys => {
      if (keys.length > 1) {
        keys.forEach(key => duplicates.add(key));
      }
    });

    return duplicates;
  };

  const getDuplicateIndicesForTool = (toolIndex: number): Set<number> => {
    const indices = new Set<number>();
    duplicateSerialKeys.forEach(key => {
      const [toolIdx, serialIdx] = key.split('-').map(Number);
      if (toolIdx === toolIndex) indices.add(serialIdx);
    });
    return indices;
  };

  useEffect(() => {
    const duplicates = findDuplicateSerialKeys();
    setDuplicateSerialKeys(duplicates);
  }, [selectedToolsState]);

  const fetchTools = async (query: string) => {
    try {
      const response = await searchReferenceProducts({ query });
      setAvailableToolsLocal(response);
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error buscando herramientas</>
      });
    }
  };

  const handleSearchTools = (query: string) => {
    if (!query || query.trim() === '') return;
    fetchTools(query);
  };

  useEffect(() => {
    return handleSearchTools(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (isSubmitting) {
      setShowConfirmationModal(false);
    }
  }, [isSubmitting]);

  return (
    <div className="addAdditionalToolsSectionContainer">
      <div className="exitButton" onClick={closeModalCallback}>
        <XSign fill={'#B3B3B3'} />
      </div>

      <h1 className="title">{content.additionalToolsTitle}</h1>

      <LogisticsOriginAndDestinationInfo cardData={cardData} />

      <div className="contentContainer">
        <div className="dropdownContainer">
          <h1 className="dropdownTitle">{content.dropdownTitle}</h1>
          <div className="dropdownContent">
            <ToolsSearchDropdown
              searchQuery={searchQuery}
              availableTools={availableTools}
              selectedToolIds={selectedToolIds}
              onSearchQueryChange={setSearchQuery}
              onToolToggle={handleToolToggle}
            />
          </div>
        </div>

        {selectedToolsState?.length > 0 && (
          <>
            <div className="toolsDivider" />
            <h1 className="selectedToolsTitle">Herramientas a agregar</h1>
            <NudosStaticAlertInComponent
              type="warning"
              color="yellow"
              customClassname="customNudosStaticAlertInComponent"
              alertOptions={{
                title: (
                  <div className="alertContent">
                    Estás agregando herramientas nuevas (no se reemplazan las del servicio). Primero se agregarán al
                    empleado y luego se moverán a Bodegas.
                    <ul className="alertList">
                      <li>Esta acción no se puede deshacer.</li>
                      <li>Podría generar costos adicionales.</li>
                    </ul>
                  </div>
                )
              }}
            />
          </>
        )}

        <div className="toolsContainer">
          {selectedToolsState?.length > 0 && (
            <>
              {selectedToolsState.map((toolState, index) => (
                <MultipleSerialToolCard
                  key={`selected-${toolState.tool.id}-${index}`}
                  tool={toolState.tool}
                  quantity={toolState.quantity}
                  serials={toolState.serials}
                  duplicateSerialIndices={getDuplicateIndicesForTool(index)}
                  onQuantityChange={quantity => handleQuantityChange(index, quantity)}
                  onSerialsChange={serials => handleSerialsChange(index, serials)}
                  onSerialErrorChange={(serialIndex, hasError) => handleSerialErrorChange(index, serialIndex, hasError)}
                  onSerialValidatingChange={(serialIndex, isValidating) =>
                    handleSerialValidatingChange(index, serialIndex, isValidating)
                  }
                  onRemove={() => removeTool(index)}
                />
              ))}
            </>
          )}
        </div>
      </div>

      <div className="confirmationButtons">
        <NudosButton
          componentSize="small"
          buttonText={content.backButton.text}
          colorPalette="secondary"
          handleClick={content.backButton.action}
        />
        <NudosButton
          componentSize="small"
          buttonText={content.nextButton.text}
          isButtonDisabled={content.nextButton.disabledCondition()}
          handleClick={content.nextButton.action}
        />
      </div>

      {isSubmitting && (
        <div className="loaderOverlay">
          <BordAnimationLoader lottieCustomClassName="h-24" />
        </div>
      )}

      {showConfirmationModal && (
        <ConfirmationModal
          verificationText="¿Estas seguro que deseas agregar nuevas herramientas que no se solicitaron en el servicio? Estas serán visibles para el cliente y pueden tener costos adicionales."
          handleConfirmation={handleConfirmAddTools}
          handleClose={handleCancelConfirmation}
          isLoading={isSubmitting}
        />
      )}
    </div>
  );
};

export default AddAdditionalToolsSection;
